import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import api, { alertsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Bell,
  DollarSign,
  Clock,
  Briefcase,
  User,
  TrendingUp,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Loader2,
  Zap,
  Target,
  Calendar,
  MapPin,
  Receipt,
  IndianRupee,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  if (!amount || amount === 0) return 'N/A';
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
  return `₹${amount.toLocaleString()}`;
};

const UrgencyBadge = ({ urgency }) => {
  const styles = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[urgency] || styles.low}`}>
      {urgency?.toUpperCase()}
    </span>
  );
};

// Recorded fees whose invoice is due (routes/placements_v2.py: /api/placements/v2/invoice-due)
// become alerts of type `invoice_due`. Unlike every other card on this page these are
// NOT estimates - the fee was recorded when the candidate was placed (Phase 5).
const invoiceAlertsFrom = (due) => {
  const rows = [];
  (due?.overdue || []).forEach((p) => rows.push({
    id: `invoice_${p.id}`,
    type: 'invoice_due',
    category: 'Fee Overdue',
    urgency: (p.days_overdue || 0) >= 30 ? 'critical' : 'high',
    title: `${p.candidate_name || 'Placement'} - fee overdue ${p.days_overdue} day${p.days_overdue === 1 ? '' : 's'}`,
    description: `${p.client_name || 'No client on file'} • joined ${String(p.joining_date || '').slice(0, 10)} • ${p.payment_due_days}-day terms • due ${p.invoice_due_date}`,
    entity_type: 'placement',
    entity_id: p.id,
    entity_name: p.candidate_name,
    potential_revenue: p.fee_amount || 0,
    recorded: true,
    ctc_confirmed: p.ctc_confirmed,
    fee_basis: p.fee_basis,
    days_overdue: p.days_overdue,
    placement: p,
    action_label: 'Open in Hired Candidates',
    action_url: '/ats/hired-candidates',
  }));
  (due?.due_soon || []).forEach((p) => rows.push({
    id: `invoice_${p.id}`,
    type: 'invoice_due',
    category: 'Fee Due Soon',
    urgency: 'medium',
    title: `${p.candidate_name || 'Placement'} - fee due ${p.days_to_due === 0 ? 'today' : `in ${p.days_to_due} day${p.days_to_due === 1 ? '' : 's'}`}`,
    description: `${p.client_name || 'No client on file'} • joined ${String(p.joining_date || '').slice(0, 10)} • ${p.payment_due_days}-day terms • due ${p.invoice_due_date}`,
    entity_type: 'placement',
    entity_id: p.id,
    entity_name: p.candidate_name,
    potential_revenue: p.fee_amount || 0,
    recorded: true,
    ctc_confirmed: p.ctc_confirmed,
    fee_basis: p.fee_basis,
    days_to_due: p.days_to_due,
    placement: p,
    action_label: 'Open in Hired Candidates',
    action_url: '/ats/hired-candidates',
  }));
  return rows;
};

const AlertCard = ({ alert, onMarkPaid }) => {
  const icons = {
    expiring_notice: Clock,
    new_high_value_job: Briefcase,
    urgent_opening: AlertTriangle,
    hot_candidate: User,
    invoice_due: Receipt,
  };
  
  const Icon = icons[alert.type] || Bell;
  
  const bgColors = {
    critical: 'border-l-red-500 bg-red-50/30',
    high: 'border-l-orange-500 bg-orange-50/30',
    medium: 'border-l-yellow-500 bg-yellow-50/30',
    low: 'border-l-slate-300 bg-slate-50/30',
  };

  return (
    <div 
      className={`p-4 rounded-lg border border-l-4 ${bgColors[alert.urgency]} hover:shadow-md transition-shadow`}
      data-testid={`alert-card-${alert.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          alert.urgency === 'critical' ? 'bg-red-100' :
          alert.urgency === 'high' ? 'bg-orange-100' :
          'bg-slate-100'
        }`}>
          <Icon className={`w-5 h-5 ${
            alert.urgency === 'critical' ? 'text-red-600' :
            alert.urgency === 'high' ? 'text-orange-600' :
            'text-slate-600'
          }`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold text-slate-900">{alert.title}</p>
              <p className="text-sm text-slate-600">{alert.description}</p>
            </div>
            <UrgencyBadge urgency={alert.urgency} />
          </div>

          {/* Details Row */}
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
            {alert.potential_revenue > 0 && (
              <span className="flex items-center gap-1 font-medium text-emerald-600">
                {alert.recorded ? <IndianRupee className="w-3 h-3" /> : <DollarSign className="w-3 h-3" />}
                {formatCurrency(alert.potential_revenue)} {alert.recorded ? 'recorded fee' : 'potential (estimate)'}
              </span>
            )}
            {alert.recorded && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${alert.ctc_confirmed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}
                title={alert.fee_basis}>
                {alert.ctc_confirmed ? 'signed CTC' : 'provisional CTC'}
              </span>
            )}
            {alert.notice_days !== undefined && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {alert.notice_days} days notice
              </span>
            )}
            {alert.days_open !== undefined && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Open {alert.days_open} days
              </span>
            )}
            {alert.openings && (
              <span className="flex items-center gap-1">
                <Target className="w-3 h-3" />
                {alert.openings} opening{alert.openings > 1 ? 's' : ''}
              </span>
            )}
            {alert.location && (
              <span className="flex items-center gap-1 truncate max-w-[150px]">
                <MapPin className="w-3 h-3" />
                {alert.location}
              </span>
            )}
          </div>

          {/* Skills */}
          {alert.skills?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {alert.skills.slice(0, 4).map((skill, i) => (
                <span key={i} className="px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600 rounded">
                  {skill.trim()}
                </span>
              ))}
            </div>
          )}

          {/* Action */}
          <div className="mt-3 flex items-center gap-4">
            <Link 
              to={alert.action_url}
              className="inline-flex items-center gap-1 text-sm font-medium text-purple-600 hover:text-purple-700"
            >
              {alert.action_label}
              <ChevronRight className="w-4 h-4" />
            </Link>
            {alert.recorded && onMarkPaid && (
              <button
                type="button"
                onClick={() => onMarkPaid(alert.placement)}
                className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:text-emerald-700"
                data-testid={`mark-paid-${alert.entity_id}`}
              >
                <CheckCircle className="w-4 h-4" />
                Mark invoice paid
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function MoneyAlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [invoiceDue, setInvoiceDue] = useState(null);   // /api/placements/v2/invoice-due
  const [money, setMoney] = useState(null);             // /api/placements/v2/summary

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    setLoading(true);
    // The estimates (server.py /api/money-alerts) and the recorded fees
    // (placements_v2) load independently: one failing never hides the other.
    let estimates = [];
    let estimateStats = {};
    try {
      const response = await alertsApi.getMoneyAlerts();
      estimates = response.data.alerts || [];
      estimateStats = {
        total: response.data.total_alerts,
        critical: response.data.critical_count,
        high: response.data.high_count,
        totalRevenue: response.data.total_potential_revenue,
      };
    } catch (error) {
      toast.error('Failed to load alerts');
    }
    let invoiceAlerts = [];
    try {
      const [dueRes, sumRes] = await Promise.all([
        api.get('/placements/v2/invoice-due'),
        api.get('/placements/v2/summary'),
      ]);
      setInvoiceDue(dueRes.data);
      setMoney(sumRes.data);
      invoiceAlerts = invoiceAlertsFrom(dueRes.data);
    } catch (error) {
      setInvoiceDue(null);   // module not installed - page works as before
      setMoney(null);
    }
    const all = [...invoiceAlerts, ...estimates];
    setAlerts(all);
    setStats({
      total: all.length,
      critical: all.filter(a => a.urgency === 'critical').length,
      high: all.filter(a => a.urgency === 'high').length,
      totalRevenue: estimateStats.totalRevenue,
    });
    setLoading(false);
  };

  const markPaid = async (p) => {
    try {
      await api.put(`/placements/${p.id}`, { invoice_paid_at: new Date().toISOString().slice(0, 10), invoice_amount: p.fee_amount });
      toast.success(`${p.candidate_name || 'Placement'} marked paid`);
      loadAlerts();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to mark paid');
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (activeTab === 'all') return true;
    if (activeTab === 'critical') return alert.urgency === 'critical';
    if (activeTab === 'invoices') return alert.type === 'invoice_due';
    if (activeTab === 'expiring') return alert.type === 'expiring_notice';
    if (activeTab === 'jobs') return alert.type === 'new_high_value_job' || alert.type === 'urgent_opening';
    if (activeTab === 'candidates') return alert.type === 'hot_candidate' || alert.type === 'expiring_notice';
    return true;
  });

  const alertsByCategory = {
    invoice_due: alerts.filter(a => a.type === 'invoice_due'),
    expiring_notice: alerts.filter(a => a.type === 'expiring_notice'),
    new_high_value_job: alerts.filter(a => a.type === 'new_high_value_job'),
    urgent_opening: alerts.filter(a => a.type === 'urgent_opening'),
    hot_candidate: alerts.filter(a => a.type === 'hot_candidate'),
  };

  return (
    <div>
      <Header title="Money Alerts" subtitle="Time-sensitive revenue opportunities">
        <Button variant="outline" onClick={loadAlerts} disabled={loading} data-testid="refresh-alerts-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </Header>

      <div className="p-6">
        {/* Recorded money - from `placements`, not estimated */}
        {money && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-testid="money-recorded-strip">
            <Card className="bg-gradient-to-br from-emerald-500 to-green-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm">Fee Earned This Month (recorded)</p>
                    <p className="text-2xl font-bold">{formatCurrency(money.fee_amount_this_month)}</p>
                    <p className="text-xs text-emerald-100">{money.placements_this_month} placed · {money.unconfirmed_ctc_count} provisional CTC</p>
                  </div>
                  <IndianRupee className="w-10 h-10 text-emerald-200" />
                </div>
              </CardContent>
            </Card>
            <Card className={money.invoice_overdue_count > 0 ? 'border-red-300 bg-red-50' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-600 text-sm font-medium">Fees Overdue</p>
                    <p className="text-2xl font-bold text-red-700">{formatCurrency(money.invoice_overdue_amount)}</p>
                    <p className="text-xs text-slate-500">{money.invoice_overdue_count} invoice{money.invoice_overdue_count === 1 ? '' : 's'} past the client's terms</p>
                  </div>
                  <Receipt className="w-10 h-10 text-red-300" />
                </div>
              </CardContent>
            </Card>
            <Card className={money.invoice_due_soon_count > 0 ? 'border-amber-300 bg-amber-50' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-600 text-sm font-medium">Due Within 7 Days</p>
                    <p className="text-2xl font-bold text-amber-700">{formatCurrency(money.invoice_due_soon_amount)}</p>
                    <p className="text-xs text-slate-500">{money.invoice_due_soon_count} to invoice · {formatCurrency(money.invoice_unpaid_amount)} unpaid in all</p>
                  </div>
                  <Calendar className="w-10 h-10 text-amber-300" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Total Potential (estimates)</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
                </div>
                <DollarSign className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-600 text-sm font-medium">Critical</p>
                  <p className="text-2xl font-bold text-red-700">{stats.critical || 0}</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-red-300" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-600 text-sm font-medium">High Priority</p>
                  <p className="text-2xl font-bold text-orange-700">{stats.high || 0}</p>
                </div>
                <Zap className="w-10 h-10 text-orange-300" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total Alerts</p>
                  <p className="text-2xl font-bold text-slate-800">{stats.total || 0}</p>
                </div>
                <Bell className="w-10 h-10 text-slate-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : alerts.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">No Money Alerts</h3>
              <p className="text-slate-500 mt-2">
                Great! No time-sensitive opportunities at the moment.
              </p>
              <p className="text-sm text-slate-400 mt-1">
                Alerts appear when a recorded placement fee falls due, candidates have expiring notice periods, or new high-value jobs are posted.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Alert List */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="pb-2">
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                      <TabsTrigger value="all">
                        All ({alerts.length})
                      </TabsTrigger>
                      <TabsTrigger value="critical" className="text-red-600">
                        Critical ({stats.critical || 0})
                      </TabsTrigger>
                      <TabsTrigger value="invoices" className="text-emerald-700">
                        Fees Due ({alertsByCategory.invoice_due.length})
                      </TabsTrigger>
                      <TabsTrigger value="expiring">
                        Expiring Notice
                      </TabsTrigger>
                      <TabsTrigger value="jobs">
                        Jobs
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {filteredAlerts.length > 0 ? (
                      filteredAlerts.map(alert => (
                        <AlertCard key={alert.id} alert={alert} onMarkPaid={markPaid} />
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-8">
                        No alerts in this category
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right: Category Summary */}
            <div className="space-y-4">
              {invoiceDue && (
                <Card className="border-emerald-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-emerald-600" />
                      Fees To Invoice
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-slate-800">
                      {alertsByCategory.invoice_due.length}
                    </p>
                    <p className="text-sm text-slate-500">
                      {invoiceDue.overdue_count} overdue · {invoiceDue.due_soon_count} due within 7 days
                      {invoiceDue.no_joining_date_count > 0 ? ` · ${invoiceDue.no_joining_date_count} without a joining date` : ''}
                    </p>
                    {alertsByCategory.invoice_due.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {alertsByCategory.invoice_due.slice(0, 3).map(a => (
                          <p key={a.id} className="text-xs text-slate-600 truncate">
                            • {a.entity_name} ({formatCurrency(a.potential_revenue)}{a.days_overdue ? `, ${a.days_overdue}d overdue` : ''})
                          </p>
                        ))}
                      </div>
                    )}
                    <Link to="/ats/hired-candidates" className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-emerald-700 hover:underline">
                      Placements ledger <ChevronRight className="w-3 h-3" />
                    </Link>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4 text-red-500" />
                    Expiring Notice Periods
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-800">
                    {alertsByCategory.expiring_notice.length}
                  </p>
                  <p className="text-sm text-slate-500">
                    Candidates available within 15 days
                  </p>
                  {alertsByCategory.expiring_notice.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {alertsByCategory.expiring_notice.slice(0, 3).map(a => (
                        <p key={a.id} className="text-xs text-slate-600 truncate">
                          • {a.entity_name} ({a.notice_days} days)
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                    New High-Value Jobs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-800">
                    {alertsByCategory.new_high_value_job.length}
                  </p>
                  <p className="text-sm text-slate-500">
                    Posted in last 7 days with billing
                  </p>
                  {alertsByCategory.new_high_value_job.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {alertsByCategory.new_high_value_job.slice(0, 3).map(a => (
                        <p key={a.id} className="text-xs text-slate-600 truncate">
                          • {a.entity_name} ({formatCurrency(a.potential_revenue)})
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    Urgent Openings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-800">
                    {alertsByCategory.urgent_opening.length}
                  </p>
                  <p className="text-sm text-slate-500">
                    Open 30+ days - risk of losing client
                  </p>
                  {alertsByCategory.urgent_opening.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {alertsByCategory.urgent_opening.slice(0, 3).map(a => (
                        <p key={a.id} className="text-xs text-slate-600 truncate">
                          • {a.entity_name} ({a.days_open} days)
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-500" />
                    Hot Candidates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-800">
                    {alertsByCategory.hot_candidate.length}
                  </p>
                  <p className="text-sm text-slate-500">
                    High-value, not yet matched
                  </p>
                  {alertsByCategory.hot_candidate.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {alertsByCategory.hot_candidate.slice(0, 3).map(a => (
                        <p key={a.id} className="text-xs text-slate-600 truncate">
                          • {a.entity_name}
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
